const KLAVIYO_NEWSLETTER_LIST_ID = 'Xc4GH6';

const initNewsletter = () => {
  const wrapperEl = document.querySelector('[data-newsletter]');
  if (!wrapperEl) return;

  const formEl = wrapperEl.querySelector('form');
  const consentEl = wrapperEl.querySelector('#accepts-marketing');
  const emailEl = wrapperEl.querySelector('#newsletter-email');
  const consentErrorEl = wrapperEl.querySelector('[data-newsletter-consent-error]');
  const submitEl = wrapperEl.querySelector('[data-newsletter-submit]');
  if (!formEl || !consentEl || !emailEl || !submitEl) return;

  // Shopify's own spam protection on `customer`/`contact`-type forms can
  // complete a real submission independently of this file's JS - it isn't
  // reliably beaten by calling preventDefault() in the submit handler below.
  // Physically disabling the only submit control removes that race entirely:
  // a disabled submit button can't be clicked, and per spec can't act as the
  // form's implicit "press Enter to submit" button either, so both paths are
  // closed rather than raced against. emailEl.validity is still tracked by
  // the browser even with novalidate on the form (that attribute only
  // suppresses the automatic block-and-show-a-bubble behaviour, not the
  // underlying constraint validation state), so it's a reliable check here
  // for both a blank value (required) and a malformed one (type="email").
  const syncSubmitDisabled = () => {
    submitEl.disabled = !consentEl.checked || !emailEl.validity.valid;
  };
  syncSubmitDisabled();

  const showConsentError = () => {
    consentEl.setAttribute('aria-invalid', 'true');
    if (consentErrorEl) consentErrorEl.classList.remove('hidden');
    // The checkbox itself is opacity-0, so its :focus-visible outline (on the
    // label's ::before) is the only visible cue that focusing it produces.
    consentEl.focus();
  };

  const clearConsentError = () => {
    consentEl.removeAttribute('aria-invalid');
    if (consentErrorEl) consentErrorEl.classList.add('hidden');
  };

  consentEl.addEventListener('change', () => {
    syncSubmitDisabled();
    if (consentEl.checked) clearConsentError();
  });

  emailEl.addEventListener('input', syncSubmitDisabled);

  // Relying on Shopify's own customer-consent sync to Klaviyo was leaving
  // profiles as "Never Subscribed" regardless of actual consent, so this
  // calls Klaviyo's own subscribe endpoint directly (same pattern as
  // Back-in-Stock) with an explicit SUBSCRIBED consent state, alongside -
  // not instead of - the native Shopify customer form submission below.
  const subscribeToKlaviyo = async (email) => {
    const companyId = wrapperEl.dataset.klaviyoApiKey;
    if (!companyId) return;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: KLAVIYO_NEWSLETTER_LIST_ID
            }
          }
        }
      }
    };

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-06-15'
        },
        body: JSON.stringify(payload)
      });
      // fetch only rejects on network failure, not on HTTP error responses -
      // without this, a rejected/malformed request from Klaviyo (e.g. a 400)
      // fails completely silently with nothing in the console to chase.
      if (!response.ok) {
        const responseBody = await response.text();
        console.error(`Klaviyo newsletter subscription failed (${response.status}):`, responseBody);
      }
    } catch (error) {
      console.error('Klaviyo newsletter subscription error:', error);
    }
  };

  // novalidate on the form lets the email field use its own aria-invalid/
  // :user-invalid styling instead of the native tooltip, but that also
  // disables the browser's own required-checkbox enforcement for the whole
  // form - so this checks it explicitly instead. reportValidity() isn't used
  // here since the checkbox is opacity-0 (custom ::before styling on the
  // label draws the visible box) and browsers generally can't anchor a
  // native tooltip to an invisible element - showConsentError() above is the
  // visible fallback.
  formEl.addEventListener('submit', async (evt) => {
    if (!consentEl.checked || !emailEl.validity.valid) {
      evt.preventDefault();
      if (!consentEl.checked) showConsentError();
      return;
    }

    // form.submit() (unlike a button click) doesn't dispatch another submit
    // event, so this can't loop back into this same handler.
    evt.preventDefault();
    try {
      await subscribeToKlaviyo(emailEl.value);
    } finally {
      formEl.submit();
    }
  });
};

export default initNewsletter;
